import { useState } from 'react';
import { LONGUEUR_MAX_FAIT, finApresDebut } from '@colette/shared';
import { C, DEGRADE_ACTION } from '../../theme';
import { Champ, styleChamp } from './champs';
import type {
  ChecklistPayload,
  EventPayload,
  FaitPayload,
  Proposition,
  ReminderPayload,
  TaskPayload,
} from '../../api/types';

// Corriger ce que Colette a compris, AVANT que la chose n'existe.
//
// La page d'accueil promettait « tu peux corriger ou refuser n'importe quelle proposition »
// et le serveur savait le faire depuis le premier jour — seul l'ecran manquait. Corriger
// une date mal comprise remplace une nouvelle dictee, donc un appel d'IA FACTURE.
//
// Regle du formulaire : on part du contenu existant et on ne change QUE ce que
// l'utilisateur touche. Les champs que l'ecran n'affiche pas (needsConfirmation,
// recurrence, eventTitle, kind) sont repris tels quels — corriger n'est pas re-saisir.

export type PayloadCorrige =
  | EventPayload
  | TaskPayload
  | ReminderPayload
  | ChecklistPayload
  | FaitPayload;

const minutesDe = (heure: string): number => {
  const [heures, minutes] = heure.split(':').map(Number);
  return (heures ?? 0) * 60 + (minutes ?? 0);
};

/**
 * La fin d'un evenement quand son DEBUT se deplace (audit du 2026-09-27).
 *
 * Ce formulaire montre l'heure de debut, jamais l'heure de fin. Avant, deplacer le debut
 * laissait la fin ou elle etait : « 09:00-10:00 » corrige a 15:00 devenait un rendez-vous
 * de 15:00 a 10:00. Une fin qu'on ne voit pas ne peut pas se corriger : elle SUIT donc
 * le debut, en gardant la duree.
 *
 * Quand aucune fin juste n'est possible — journee entiere, proposition deja incoherente,
 * fin qui passerait minuit — il n'y a PAS de fin. Mieux vaut pas de fin qu'une fin fausse.
 *
 * Le calcul part toujours de la proposition d'ORIGINE, pas de la saisie en cours : apres
 * un detour par 23:00, revenir a 15:00 rend bien 16:00.
 */
export function finApresDeplacement(
  debutAvant: string | null,
  finAvant: string | null,
  debutApres: string | null,
): string | null {
  if (debutAvant === null || finAvant === null || debutApres === null) return null;
  const duree = minutesDe(finAvant) - minutesDe(debutAvant);
  if (duree <= 0) return null;
  const fin = minutesDe(debutApres) + duree;
  if (fin >= 24 * 60) return null;
  return `${String(Math.floor(fin / 60)).padStart(2, '0')}:${String(fin % 60).padStart(2, '0')}`;
}


export function FormulaireCorrection({
  proposition,
  occupe,
  onEnregistrer,
  onAnnuler,
}: {
  proposition: Proposition;
  occupe: boolean;
  onEnregistrer: (payload: PayloadCorrige) => void;
  onAnnuler: () => void;
}) {
  // Un etat par genre : chaque formulaire part du payload recu.
  const [evenement, setEvenement] = useState<EventPayload>(
    proposition.kind === 'EVENT' ? proposition.payload : ({} as EventPayload),
  );
  const [tache, setTache] = useState<TaskPayload>(
    proposition.kind === 'TASK' ? proposition.payload : ({} as TaskPayload),
  );
  const [rappel, setRappel] = useState<ReminderPayload>(
    proposition.kind === 'REMINDER' ? proposition.payload : ({} as ReminderPayload),
  );
  const [liste, setListe] = useState<ChecklistPayload>(
    proposition.kind === 'CHECKLIST' ? proposition.payload : ({} as ChecklistPayload),
  );
  // Un fait se corrige dans son TEXTE ; son sujet est repris tel quel (etape B2).
  const [fait, setFait] = useState<FaitPayload>(
    proposition.kind === 'FACT' ? proposition.payload : ({} as FaitPayload),
  );
  // Les elements d'une liste se corrigent en texte, une ligne par element : c'est le
  // geste le plus court pour rayer, ajouter ou reformuler d'un coup.
  const [elements, setElements] = useState(
    proposition.kind === 'CHECKLIST' ? proposition.payload.items.join('\n') : '',
  );

  const soumettre = () => {
    if (occupe) return;
    switch (proposition.kind) {
      case 'EVENT':
        // Une fin qui ne vient pas apres le debut (« soirée 22:00-01:00 » rendue par le
        // cerveau) serait refusee par le serveur — et ce formulaire n'a aucun champ pour
        // la corriger. Elle part donc sans fin plutot que de laisser l'utilisateur
        // devant un refus qu'il ne peut pas lever.
        onEnregistrer(
          finApresDebut(evenement.time, evenement.endTime)
            ? evenement
            : { ...evenement, endTime: null },
        );
        break;
      case 'TASK':
        onEnregistrer(tache);
        break;
      case 'REMINDER':
        onEnregistrer(rappel);
        break;
      case 'CHECKLIST':
        onEnregistrer({
          ...liste,
          items: elements
            .split('\n')
            .map((ligne) => ligne.trim())
            .filter((ligne) => ligne !== ''),
        });
        break;
      case 'FACT':
        onEnregistrer(fait);
        break;
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        soumettre();
      }}
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      {proposition.kind === 'EVENT' ? (
        <>
          <Champ libelle="Titre">
            <input
              value={evenement.title}
              onChange={(e) => setEvenement({ ...evenement, title: e.target.value })}
              aria-label="Titre"
              style={styleChamp}
            />
          </Champ>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1.4, minWidth: 0 }}>
              <Champ libelle="Jour">
                <input
                  type="date"
                  value={evenement.date}
                  onChange={(e) => setEvenement({ ...evenement, date: e.target.value })}
                  aria-label="Jour"
                  style={styleChamp}
                />
              </Champ>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Champ libelle="Heure">
                <input
                  type="time"
                  value={evenement.time ?? ''}
                  onChange={(e) => {
                    const time = e.target.value === '' ? null : e.target.value;
                    // La fin, invisible ici, suit le debut : voir `finApresDeplacement`.
                    const origine = proposition.payload as EventPayload;
                    setEvenement({
                      ...evenement,
                      time,
                      endTime: finApresDeplacement(origine.time, origine.endTime, time),
                    });
                  }}
                  aria-label="Heure"
                  style={styleChamp}
                />
              </Champ>
            </div>
          </div>
          <Champ libelle="Lieu">
            <input
              value={evenement.location ?? ''}
              onChange={(e) =>
                setEvenement({ ...evenement, location: e.target.value === '' ? null : e.target.value })
              }
              aria-label="Lieu"
              placeholder="Facultatif"
              style={styleChamp}
            />
          </Champ>
        </>
      ) : null}

      {proposition.kind === 'TASK' ? (
        <>
          <Champ libelle="Titre">
            <input
              value={tache.title}
              onChange={(e) => setTache({ ...tache, title: e.target.value })}
              aria-label="Titre"
              style={styleChamp}
            />
          </Champ>
          <Champ libelle="À faire avant">
            <input
              type="date"
              value={tache.dueDate ?? ''}
              onChange={(e) =>
                setTache({ ...tache, dueDate: e.target.value === '' ? null : e.target.value })
              }
              aria-label="À faire avant"
              style={styleChamp}
            />
          </Champ>
        </>
      ) : null}

      {proposition.kind === 'REMINDER' ? (
        <>
          <Champ libelle="Message">
            <input
              value={rappel.message}
              onChange={(e) => setRappel({ ...rappel, message: e.target.value })}
              aria-label="Message"
              style={styleChamp}
            />
          </Champ>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1.4, minWidth: 0 }}>
              <Champ libelle="Jour">
                <input
                  type="date"
                  value={rappel.date}
                  onChange={(e) => setRappel({ ...rappel, date: e.target.value })}
                  aria-label="Jour"
                  style={styleChamp}
                />
              </Champ>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Champ libelle="Heure">
                <input
                  type="time"
                  value={rappel.time}
                  onChange={(e) => setRappel({ ...rappel, time: e.target.value })}
                  aria-label="Heure"
                  style={styleChamp}
                />
              </Champ>
            </div>
          </div>
        </>
      ) : null}

      {proposition.kind === 'CHECKLIST' ? (
        <>
          <Champ libelle="Titre">
            <input
              value={liste.title}
              onChange={(e) => setListe({ ...liste, title: e.target.value })}
              aria-label="Titre"
              style={styleChamp}
            />
          </Champ>
          <Champ libelle="Éléments">
            <textarea
              value={elements}
              onChange={(e) => setElements(e.target.value)}
              aria-label="Éléments"
              rows={5}
              style={{ ...styleChamp, height: 'auto', padding: '10px 12px', lineHeight: 1.5 }}
            />
          </Champ>
          <div style={{ fontSize: 12, color: C.tertiaire, marginTop: -6 }}>
            Un élément par ligne.
          </div>
        </>
      ) : null}

      {proposition.kind === 'FACT' ? (
        <>
          <Champ libelle="Ce que Colette doit retenir">
            <input
              value={fait.texte}
              onChange={(e) => setFait({ ...fait, texte: e.target.value })}
              aria-label="Ce que Colette doit retenir"
              maxLength={LONGUEUR_MAX_FAIT}
              style={styleChamp}
            />
          </Champ>
          <div style={{ fontSize: 12, color: C.tertiaire, marginTop: -6 }}>
            {fait.sujet === null ? 'Concerne tout le foyer' : `Concerne ${fait.sujet}`}
          </div>
        </>
      ) : null}

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="submit"
          disabled={occupe}
          className="pressable"
          style={{
            flex: 1,
            height: 44,
            border: 'none',
            borderRadius: 12,
            background: occupe ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 14.5,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: occupe ? 'default' : 'pointer',
          }}
        >
          {occupe ? 'Enregistrement…' : 'Enregistrer et valider'}
        </button>
        <button
          type="button"
          className="pressable"
          aria-label="Annuler la correction"
          onClick={onAnnuler}
          style={{
            height: 44,
            padding: '0 16px',
            border: `1px solid ${C.bordure}`,
            borderRadius: 12,
            background: C.carte,
            color: C.secondaire,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </div>
    </form>
  );
}
